import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import Pagination from '../../components/common/Pagination';
import SupplierCatalogueModal from './SupplierCatalogueModal';
import PurchaseOrderModal from './PurchaseOrderModal';
import PurchaseOrderReceptionModal from './PurchaseOrderReceptionModal';
import GenericImportModal from '../../components/common/GenericImportModal';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';

const SupplierDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [supplier, setSupplier] = useState(null);
    const [catalogue, setCatalogue] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modals
    const [isCatalogueModalOpen, setIsCatalogueModalOpen] = useState(false);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedCatalogItemIds, setSelectedCatalogItemIds] = useState([]);
    const [initialOrderItems, setInitialOrderItems] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    // Search & Pagination (Catalog)
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Pagination (Orders)
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersItemsPerPage, setOrdersItemsPerPage] = useState(10);

    const [activeTab, setActiveTab] = useState('info'); // 'info', 'orders', 'catalog'

    // Supplier form state (for Tab 1)
    const [formData, setFormData] = useState({
        name: '', contactEmail: '', contactPhone: '', category: '',
        address: '', website: '', status: 'Actif', deliveryScore: 0,
        avgDeliveryTime: '', paymentTerms: 'Net 30', paymentMethod: 'Virement bancaire',
        currency: 'EUR', earlyDiscount: '', certifications: '', notes: '', francoPort: '',
        coordinates: null
    });
    const [categories, setCategories] = useState([]);

    const filteredItems = useMemo(() => {
        return catalogue.filter(p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.sku.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [catalogue, searchQuery]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const displayedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Orders Pagination
    const ordersTotalPages = Math.ceil(orders.length / ordersItemsPerPage);
    const displayedOrders = orders.slice((ordersPage - 1) * ordersItemsPerPage, ordersPage * ordersItemsPerPage);

    const fetchAll = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const [supRes, catRes, ordRes, categoriesRes] = await Promise.all([
                fetch(`/api/suppliers/${id}`),
                fetch(`/api/suppliers/${id}/products?companyId=${user.companyId}`),
                fetch(`/api/purchase-orders?companyId=${user.companyId}&supplierId=${id}`),
                fetch(`/api/categories?companyId=${user.companyId}`)
            ]);

            if (supRes.ok) {
                const supData = await supRes.json();
                setSupplier(supData);
                setFormData({
                    name: supData.name, contactEmail: supData.contactEmail || '', contactPhone: supData.contactPhone || '',
                    category: supData.category || '', address: supData.address || '', website: supData.website || '',
                    status: supData.status, deliveryScore: supData.deliveryScore || 0,
                    avgDeliveryTime: supData.avgDeliveryTime || '', paymentTerms: supData.paymentTerms || 'Net 30',
                    paymentMethod: supData.paymentMethod || 'Virement bancaire', currency: supData.currency || 'EUR',
                    earlyDiscount: supData.earlyDiscount || '', certifications: supData.certifications || '', notes: supData.notes || '', francoPort: supData.francoPort || '',
                    coordinates: supData.coordinates || null
                });
            }
            if (catRes.ok) {
                const productsData = await catRes.json();
                const catalogItemsRes = await fetch(`/api/suppliers/${id}/catalog-items?companyId=${user.companyId}`);
                const catalogOnlyData = catalogItemsRes.ok ? await catalogItemsRes.json() : [];

                // Deduplication: SupplierCatalogItem entries are the ONLY master records for this tab.
                // We enrich them with stock levels from the inventory (ProductSupplier links).
                const inventoryProducts = productsData;

                const catalogEntries = catalogOnlyData.map(item => {
                    const itemSku = item.sku?.trim().toLowerCase();
                    const itemBarcode = item.barcode?.trim();

                    const match = inventoryProducts.find(p => {
                        const pSku = p.sku?.trim().toLowerCase();
                        const pBarcode = p.barcode?.trim();
                        return (itemSku && pSku && itemSku === pSku) ||
                            (itemBarcode && pBarcode && itemBarcode === pBarcode);
                    });

                    if (match) {
                        return {
                            ...item,
                            isFromInventory: false,
                            isCatalogItem: true,
                            inventoryStock: match.quantity,
                            ProductSupplier: match.ProductSupplier
                        };
                    }
                    return { ...item, isFromInventory: false, isCatalogItem: true };
                });

                // The user specifically requested that the catalog only list catalog products.
                // Inventory-only links (without a corresponding catalog entry) are removed from this view
                // to prevent any form of duplication or confusion.
                const merged = catalogEntries.sort((a, b) => a.name.localeCompare(b.name));

                setCatalogue(merged);
            }
            if (ordRes.ok) {
                const ordData = await ordRes.json();
                setOrders(Array.isArray(ordData.orders) ? ordData.orders : []);
            }
            if (categoriesRes.ok) {
                const catData = await categoriesRes.json();
                setCategories(catData.map(c => c.name));
            }
        } catch (err) {
            console.error(err);
            showToast("Erreur de chargement", "error");
        } finally {
            setLoading(false);
        }
    }, [id, user?.companyId, showToast]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Refetch helpers
    const handleAddCatalogueSuccess = () => fetchAll();
    const handleOrderCreated = () => fetchAll();

    const handleAddProduct = () => {
        setSelectedProduct(null);
        setIsCatalogueModalOpen(true);
    };

    const handleEditProduct = (product) => {
        setSelectedProduct(product);
        setIsCatalogueModalOpen(true);
    };

    const handleDeleteProduct = (product) => {
        showConfirm({
            title: 'Retirer du catalogue ?',
            message: `Voulez-vous vraiment retirer ${product.name} du catalogue de ce fournisseur ?`,
            type: 'danger',
            onConfirm: async () => {
                try {
                    const url = product.isFromInventory
                        ? `/api/suppliers/${id}/products/${product.id}`
                        : `/api/suppliers/${id}/catalog-items/${product.id}`;
                    const res = await fetch(url, { method: 'DELETE' });
                    if (res.ok) {
                        showToast("Article retiré du catalogue", "success");
                        fetchAll();
                    } else {
                        showToast("Erreur lors de la suppression", "error");
                    }
                } catch (err) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };

    const handleDuplicate = async (e, order) => {
        e.stopPropagation();
        try {
            const res = await fetch(`/api/purchase-orders/${order.id}/duplicate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.id })
            });
            if (res.ok) {
                const newOrder = await res.json();
                showToast("Commande dupliquée en brouillon", "success");
                fetchAll();
                setSelectedOrder(newOrder);
                setIsOrderModalOpen(true);
            } else {
                const data = await res.json();
                showToast(data.error || "Erreur de duplication", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleDownloadPDF = async (orderId, reference) => {
        try {
            const res = await fetch(`/api/purchase-orders/${orderId}/pdf`);
            if (!res.ok) throw new Error("Erreur serveur");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Commande_${reference}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showToast("Erreur lors du téléchargement", "error");
        }
    };

    const handleMarkDelivered = async (e, orderId) => {
        e.stopPropagation();
        showConfirm({
            title: 'Marquer comme livrée ?',
            message: 'Voulez-vous vraiment marquer cette commande comme livrée ? Cela notifiera les opérateurs pour la réception.',
            type: 'info',
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/purchase-orders/${orderId}/mark-delivered`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId: user.id })
                    });
                    if (res.ok) {
                        showToast("Commande marquée comme livrée", "success");
                        fetchAll();
                    } else {
                        const data = await res.json();
                        showToast(data.error || "Erreur de mise à jour", "error");
                    }
                } catch (error) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };

    const handleSupplierUpdate = async (e) => {
        e.preventDefault();
        try {
            let coords = formData.coordinates;
            // Si pas de coordonnées mais une adresse, on tente un géocodage de secours
            if (!coords && formData.address) {
                const resGeo = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.address)}&limit=1`);
                const dataGeo = await resGeo.json();
                if (dataGeo && dataGeo.length > 0) {
                    coords = { lat: parseFloat(dataGeo[0].lat), lng: parseFloat(dataGeo[0].lon) };
                }
            }

            const res = await fetch(`/api/suppliers/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, coordinates: coords, companyId: user.companyId })
            });
            if (res.ok) {
                showToast("Informations mises à jour", "success");
                fetchAll();
            } else {
                const data = await res.json();
                showToast(data.error || "Erreur de sauvegarde", "error");
            }
        } catch (err) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleImport = async (data) => {
        try {
            const res = await fetch(`/api/suppliers/${id}/catalog-items/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: data,
                    companyId: user.companyId
                })
            });

            const result = await res.json();

            if (res.ok) {
                fetchAll();
                return result;
            } else {
                showToast(result.error || "Erreur lors de l'importation", "error");
                throw new Error(result.error);
            }
        } catch (error) {
            showToast("Erreur lors de l'importation", "error");
            throw error;
        }
    };

    const importFields = [
        { key: 'name', label: 'Nom du produit', required: true },
        { key: 'sku', label: 'SKU / Référence interne' },
        { key: 'barcode', label: 'Code-barres / EAN' },
        { key: 'supplierRef', label: 'Référence Fournisseur' },
        { key: 'purchasePrice', label: 'Prix d\'achat' },
        { key: 'leadTime', label: 'Délai (jours)' },
        { key: 'category', label: 'Catégorie' },
        { key: 'description', label: 'Description' }
    ];

    const handleExport = () => {
        try {
            const exportData = catalogue.map(p => ({
                'Nom du produit': p.name,
                'SKU / Réf Interne': p.sku || '',
                'Code-barres / EAN': p.barcode || '',
                'Référence Fournisseur': (p.ProductSupplier?.supplierRef || p.supplierRef) || '',
                'Prix d\'achat (€)': (p.ProductSupplier?.purchasePrice || p.purchasePrice) || 0,
                'Délai (jours)': (p.ProductSupplier?.leadTime || p.leadTime) || 0,
                'Catégorie': p.category?.name || '',
                'Description': p.description || '',
                'Source': p.isFromInventory ? 'Inventaire' : 'Catalogue'
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Catalogue');

            const date = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Catalogue_${supplier.name.replace(/\s+/g, '_')}_${date}.xlsx`);
            showToast("Catalogue exporté avec succès", "success");
        } catch (err) {
            console.error('Export error:', err);
            showToast("Erreur lors de l'exportation", "error");
        }
    };

    const handleBulkDelete = () => {
        const catIds = selectedCatalogItemIds
            .filter(id => id.startsWith('cat-'))
            .map(id => parseInt(id.replace('cat-', '')));

        if (catIds.length === 0) {
            showToast("Sélectionnez des articles du catalogue à supprimer", "info");
            return;
        }

        showConfirm({
            title: 'Supprimer les articles ?',
            message: `Voulez-vous vraiment supprimer les ${catIds.length} articles sélectionnés du catalogue ?`,
            type: 'danger',
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/suppliers/${id}/catalog-items/bulk-delete`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ itemIds: catIds })
                    });
                    if (res.ok) {
                        showToast(`${catIds.length} articles supprimés`, "success");
                        setSelectedCatalogItemIds([]);
                        fetchAll();
                    } else {
                        showToast("Erreur lors de la suppression", "error");
                    }
                } catch (err) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };



    if (loading) return <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined animate-spin text-4xl text-primary">progress_activity</span></div>;
    if (!supplier) return <div className="text-center py-20 text-slate-500">Fournisseur non trouvé.</div>;

    const getInitials = (name) => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Brouillon': return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
            case 'Envoyée': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'Partiellement Livrée': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
            case 'Livrée': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'Clôturée': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            default: return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
        }
    };

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex justify-between items-end border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/suppliers')} className="size-10 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center text-slate-500 hover:text-primary transition-colors shadow-sm">
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-3xl font-black leading-tight tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">{supplier.name}</h1>
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${supplier.status === 'Actif' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                {supplier.status}
                            </span>
                        </div>
                        <p className="text-slate-500 dark:text-slate-400 font-medium">Fournisseur — ID: FR-{String(id).padStart(4, '0')}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {activeTab === 'catalog' && (
                        <>
                            <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 rounded-lg font-bold transition-all text-xs">
                                <span className="material-symbols-outlined text-[18px]">upload</span>
                                Importer
                            </button>
                            <button onClick={handleExport} className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-bold transition-all text-xs">
                                <span className="material-symbols-outlined text-[18px]">download</span>
                                Exporter
                            </button>
                            {selectedCatalogItemIds.some(id => id.startsWith('cat-')) && (
                                <button onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 hover:bg-rose-200 dark:hover:bg-rose-800 rounded-lg font-bold transition-all text-xs">
                                    <span className="material-symbols-outlined text-[18px]">delete_sweep</span>
                                    Supprimer ({selectedCatalogItemIds.filter(id => id.startsWith('cat-')).length})
                                </button>
                            )}
                            <button onClick={handleAddProduct} className="flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg font-bold transition-all text-xs">
                                <span className="material-symbols-outlined text-[18px]">add</span>
                                Ajouter
                            </button>
                            {selectedCatalogItemIds.length > 0 && (
                                <button onClick={() => {
                                    const selectedItemsToOrder = catalogue.filter(p => selectedCatalogItemIds.includes(p.isFromInventory ? `inv-${p.id}` : `cat-${p.id}`));
                                    setInitialOrderItems(selectedItemsToOrder);
                                    setSelectedOrder(null);
                                    setIsOrderModalOpen(true);
                                }} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg font-bold transition-all shadow-lg shadow-emerald-500/20 text-xs ml-1">
                                    <span className="material-symbols-outlined text-[18px]">shopping_cart_checkout</span>
                                    Commande ({selectedCatalogItemIds.length})
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Quick Stats Banner */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center gap-4">
                    <div className="size-10 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
                        <span className="material-symbols-outlined">shopping_bag</span>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Commandes</p>
                        <p className="text-lg font-bold">{orders.length}</p>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center gap-4">
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <span className="material-symbols-outlined">inventory_2</span>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Catalogue</p>
                        <p className="text-lg font-bold">{catalogue.length}</p>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center gap-4">
                    <div className="size-10 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
                        <span className="material-symbols-outlined">schedule</span>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Délai moyen</p>
                        <p className="text-lg font-bold">{supplier.avgDeliveryTime || 'N/A'}</p>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center gap-4">
                    <div className="size-10 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                        <span className="material-symbols-outlined">star</span>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Score</p>
                        <p className="text-lg font-bold">{supplier.deliveryScore}/5</p>
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="flex border-b border-slate-200 dark:border-slate-800 gap-8">
                <button
                    onClick={() => setActiveTab('info')}
                    className={`flex items-center gap-2 border-b-2 pb-3 pt-2 text-sm font-bold transition-all ${activeTab === 'info' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    <span className="material-symbols-outlined text-[18px]">info</span>
                    Informations Générales
                </button>
                <button
                    onClick={() => setActiveTab('orders')}
                    className={`flex items-center gap-2 border-b-2 pb-3 pt-2 text-sm font-bold transition-all ${activeTab === 'orders' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    <span className="material-symbols-outlined text-[18px]">receipt_long</span>
                    Commandes
                </button>
                <button
                    onClick={() => setActiveTab('catalog')}
                    className={`flex items-center gap-2 border-b-2 pb-3 pt-2 text-sm font-bold transition-all ${activeTab === 'catalog' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    <span className="material-symbols-outlined text-[18px]">inventory</span>
                    Catalogue & Tarifs
                </button>
            </div>

            {/* Tab Content */}
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                {activeTab === 'info' && (
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <h3 className="text-lg font-bold">Fiche Fournisseur</h3>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-slate-400 uppercase">Dernière modif : {new Date(supplier.updatedAt).toLocaleDateString()}</span>
                            </div>
                        </div>
                        <form onSubmit={handleSupplierUpdate} className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <div className="space-y-6">
                                    <h4 className="text-xs font-black uppercase text-primary tracking-widest flex items-center gap-2">
                                        <span className="size-1.5 rounded-full bg-primary"></span>
                                        Identité
                                    </h4>
                                    <div className="space-y-4">
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Nom de l'entreprise</label>
                                            <input required className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-semibold" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Catégorie</label>
                                            <select className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-semibold" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                                                <option value="">Sélectionner...</option>
                                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Statut</label>
                                            <select className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-semibold" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                                <option value="Actif">Actif</option>
                                                <option value="Inactif">Inactif</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <h4 className="text-xs font-black uppercase text-primary tracking-widest flex items-center gap-2">
                                        <span className="size-1.5 rounded-full bg-primary"></span>
                                        Contact & Localisation
                                    </h4>
                                    <div className="space-y-4">
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Email principal</label>
                                            <div className="relative">
                                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">mail</span>
                                                <input type="email" className="w-full h-11 pl-10 pr-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-semibold" value={formData.contactEmail} onChange={e => setFormData({ ...formData, contactEmail: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Téléphone</label>
                                            <div className="relative">
                                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">call</span>
                                                <input className="w-full h-11 pl-10 pr-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-semibold" value={formData.contactPhone} onChange={e => setFormData({ ...formData, contactPhone: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Adresse</label>
                                            <AddressAutocomplete
                                                value={formData.address}
                                                onChange={(val) => setFormData({ ...formData, address: val })}
                                                onSelect={(addr, coords) => setFormData({ ...formData, address: addr, coordinates: coords })}
                                            />
                                            {formData.coordinates && (
                                                <p className="text-[10px] text-emerald-500 font-bold flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-[12px]">check_circle</span>
                                                    Localisé : {formData.coordinates.lat.toFixed(4)}, {formData.coordinates.lng.toFixed(4)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <h4 className="text-xs font-black uppercase text-primary tracking-widest flex items-center gap-2">
                                        <span className="size-1.5 rounded-full bg-primary"></span>
                                        Logistique & Paiement
                                    </h4>
                                    <div className="space-y-4">
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Conditions de paiement</label>
                                            <input className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-semibold" value={formData.paymentTerms} onChange={e => setFormData({ ...formData, paymentTerms: e.target.value })} />
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Délai moyen de livraison (jours)</label>
                                            <div className="relative">
                                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">schedule</span>
                                                <input className="w-full h-11 pl-10 pr-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-semibold" value={formData.avgDeliveryTime} onChange={e => setFormData({ ...formData, avgDeliveryTime: e.target.value })} placeholder="Ex: 5" />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Franco de port (€)</label>
                                            <div className="relative">
                                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">payments</span>
                                                <input type="number" step="0.01" className="w-full h-11 pl-10 pr-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono font-bold" value={formData.francoPort} onChange={e => setFormData({ ...formData, francoPort: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Note / Commentaires</label>
                                            <textarea className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium resize-none h-24" value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-12 pt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                                <button type="submit" className="flex items-center gap-2 px-8 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-all shadow-lg shadow-primary/20">
                                    <span className="material-symbols-outlined">save</span>
                                    Enregistrer les modifications
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {activeTab === 'orders' && (
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">history</span>
                                Historique des commandes
                            </h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400">Référence</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-center">Date</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400">Créateur</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-right">Montant</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-center">Statut</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {orders.length === 0 ? (
                                        <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic">Aucune commande.</td></tr>
                                    ) : (
                                        displayedOrders.map(o => (
                                            <tr key={o.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer" onClick={() => {
                                                if (o.status === 'Brouillon') {
                                                    setSelectedOrder(o);
                                                    setIsOrderModalOpen(true);
                                                } else {
                                                    setSelectedOrder(o);
                                                }
                                            }}>
                                                <td className="px-6 py-4 font-bold font-mono text-slate-900 dark:text-white uppercase">
                                                    <div className="flex flex-col">
                                                        <span>{o.reference}</span>
                                                        {o.erpReference && <span className="text-[10px] text-primary lowercase tracking-normal">ERP: {o.erpReference}</span>}
                                                        {o.supplierReference && <span className="text-[10px] text-slate-400 font-normal lowercase tracking-normal">Fourn: {o.supplierReference}</span>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-center text-slate-600 dark:text-slate-400">{new Date(o.orderDate || o.createdAt).toLocaleDateString()}</td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        {o.creator ? (
                                                            o.creator.avatar ? (
                                                                <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 border border-slate-200 dark:border-slate-700">
                                                                    <img src={o.creator.avatar} alt="Avatar" className="w-full h-full object-cover" />
                                                                </div>
                                                            ) : (
                                                                <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-black border border-primary/20 shrink-0">
                                                                    {o.creator.name ? o.creator.name.charAt(0).toUpperCase() : '?'}
                                                                </div>
                                                            )
                                                        ) : (
                                                            <span className="text-slate-400 italic text-xs">Système</span>
                                                        )}
                                                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                            {o.creator?.name || ''}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right font-bold text-slate-700 dark:text-slate-300">{parseFloat(o.totalHT || 0).toFixed(2)} €</td>
                                                <td className="px-6 py-4">
                                                    <div className="flex justify-center">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${getStatusStyle(o.status)}`}>
                                                            {o.status}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                                                    <div className="flex justify-end items-center gap-1">
                                                        <button onClick={() => handleDownloadPDF(o.id, o.reference)} className="p-2 text-slate-400 hover:text-blue-500 rounded-lg transition-colors border border-transparent hover:border-blue-100" title="Bon de commande">
                                                            <span className="material-symbols-outlined text-[18px]">picture_as_pdf</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDuplicate(e, o); }}
                                                            className="p-2 text-slate-400 hover:text-primary rounded-lg transition-colors border border-transparent hover:border-primary/20"
                                                            title="Dupliquer la commande (Repasser)"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">content_copy</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setSelectedOrder(o); setIsOrderModalOpen(true); }}
                                                            disabled={!(o.status === 'Brouillon' || o.status === 'Envoyée')}
                                                            className={`p-2 rounded-lg transition-colors border border-transparent ${!(o.status === 'Brouillon' || o.status === 'Envoyée') ? 'text-slate-300 opacity-50 cursor-not-allowed' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-100 hover:border-amber-200'}`}
                                                            title="Modifier la commande"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => handleMarkDelivered(e, o.id)}
                                                            disabled={o.status !== 'Envoyée'}
                                                            className={`p-2 rounded-lg transition-colors border border-transparent ${o.status !== 'Envoyée' ? 'text-slate-300 opacity-50 cursor-not-allowed' : 'text-primary hover:bg-primary/10 hover:border-primary/20'}`}
                                                            title="Marquer Livrée"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">local_shipping</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setSelectedOrder(o); }}
                                                            disabled={!(o.status === 'Envoyée' || o.status === 'Livrée' || o.status === 'Partiellement Livrée')}
                                                            className={`p-2 rounded-lg transition-colors border border-transparent ${!(o.status === 'Envoyée' || o.status === 'Livrée' || o.status === 'Partiellement Livrée') ? 'text-slate-300 opacity-50 cursor-not-allowed' : 'text-amber-600 hover:bg-amber-100 hover:border-amber-200'}`}
                                                            title="Pointer la réception"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">inventory_2</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-6 border-t border-slate-100 dark:border-slate-800">
                            <Pagination
                                currentPage={ordersPage}
                                totalPages={ordersTotalPages}
                                onPageChange={setOrdersPage}
                                limit={ordersItemsPerPage}
                                onLimitChange={setOrdersItemsPerPage}
                                totalItems={orders.length}
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'catalog' && (
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 space-y-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">inventory</span>
                                Catalogue & Tarifs
                            </h3>

                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">search</span>
                                <input type="text" placeholder="Rechercher dans le catalogue..." value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium" />
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                        <th className="px-6 py-4 w-12 text-center">
                                            <input
                                                type="checkbox"
                                                className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                                                checked={displayedItems.length > 0 && displayedItems.every(p => selectedCatalogItemIds.includes(p.isFromInventory ? `inv-${p.id}` : `cat-${p.id}`))}
                                                onChange={(e) => {
                                                    const displayedIds = displayedItems.map(p => p.isFromInventory ? `inv-${p.id}` : `cat-${p.id}`);
                                                    if (e.target.checked) {
                                                        const newSelected = [...new Set([...selectedCatalogItemIds, ...displayedIds])];
                                                        setSelectedCatalogItemIds(newSelected);
                                                    } else {
                                                        setSelectedCatalogItemIds(selectedCatalogItemIds.filter(id => !displayedIds.includes(id)));
                                                    }
                                                }}
                                            />
                                        </th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400">Produit</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400">Réf. Fournisseur</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-right">Prix d'achat</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-center">Délai</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-right">Stock</th>
                                        <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {displayedItems.length === 0 ? (
                                        <tr><td colSpan="7" className="px-6 py-12 text-center text-slate-400 italic">Aucun article trouvé.</td></tr>
                                    ) : (
                                        displayedItems.map(p => {
                                            const itemId = p.isFromInventory ? `inv-${p.id}` : `cat-${p.id}`;
                                            const isSelected = selectedCatalogItemIds.includes(itemId);
                                            return (
                                                <tr key={itemId} className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors ${isSelected ? 'bg-primary/5 dark:bg-primary/10' : ''}`}>
                                                    <td className="px-6 py-4 text-center">
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4 cursor-pointer"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedCatalogItemIds([...selectedCatalogItemIds, itemId]);
                                                                } else {
                                                                    setSelectedCatalogItemIds(selectedCatalogItemIds.filter(id => id !== itemId));
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-bold text-slate-900 dark:text-white">{p.name}</p>
                                                            {(p.isCatalogItem || !p.isFromInventory) && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 text-[9px] font-black uppercase">Catalogue</span>}
                                                        </div>
                                                        <p className="text-xs text-slate-500 font-mono italic">{p.sku}</p>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-mono text-slate-500">{(p.ProductSupplier?.supplierRef || p.supplierRef) || '—'}</td>
                                                    <td className="px-6 py-4 text-right font-black text-slate-700 dark:text-slate-300">
                                                        {(p.ProductSupplier?.purchasePrice || p.purchasePrice) ?
                                                            `${Number(p.ProductSupplier?.purchasePrice || p.purchasePrice).toFixed(2)} €` :
                                                            '—'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-center">{(p.ProductSupplier?.leadTime || p.leadTime) ? `${p.ProductSupplier?.leadTime || p.leadTime} j` : '—'}</td>
                                                    <td className="px-6 py-4 text-right font-bold text-primary">
                                                        {p.isFromInventory ? p.quantity : (
                                                            p.inventoryStock !== undefined ? p.inventoryStock : <span className="text-slate-400 font-normal italic text-xs">N/A</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <button onClick={() => handleEditProduct(p)} className="p-1.5 text-slate-400 hover:text-primary transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" title="Modifier"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                                                            <button onClick={() => handleDeleteProduct(p)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" title="Retirer"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-6 border-t border-slate-100 dark:border-slate-800">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                limit={itemsPerPage}
                                onLimitChange={setItemsPerPage}
                                totalItems={filteredItems.length}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            <SupplierCatalogueModal
                isOpen={isCatalogueModalOpen}
                onClose={() => setIsCatalogueModalOpen(false)}
                supplierId={id}
                onAdded={handleAddCatalogueSuccess}
                initialProduct={selectedProduct}
            />
            <PurchaseOrderModal
                isOpen={isOrderModalOpen}
                onClose={() => { setIsOrderModalOpen(false); setSelectedOrder(null); setInitialOrderItems([]); setSelectedCatalogItemIds([]); }}
                supplier={supplier}
                catalogue={catalogue}
                onCreated={handleOrderCreated}
                order={selectedOrder}
                initialItems={initialOrderItems}
            />
            <PurchaseOrderReceptionModal
                isOpen={!!selectedOrder && selectedOrder.status !== 'Brouillon'}
                order={selectedOrder}
                onClose={() => setSelectedOrder(null)}
                onUpdated={handleOrderCreated}
            />
            <GenericImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                fields={importFields}
                onImport={handleImport}
                title={`Importer dans : ${supplier.name}`}
            />
        </div >
    );
};

export default SupplierDetail;
